<template>
  <div class="video" :style="{ height:videoWrapperHeight }" :class="videoClass">
    <audio v-if="isAmazonChime" id="meeting-audio" style="display:none"/>

    <!-- Reconnecting Overlay -->
    <div v-if="showReconnectingOverlay" class="reconnecting-overlay">
      <div class="reconnecting-message">
        <span class="reconnecting-spinner"></span>
        <span class="reconnecting-title">{{ $t('videoMessage.reconnecting') }}</span>
        <span class="reconnecting-subtext">{{ $t('videoMessage.reconnectingSubtext1') }}</span>
        <span class="reconnecting-subtext">{{ $t('videoMessage.reconnectingSubtext2') }}</span>
      </div>
    </div>

    <!-- Online Status Indicator -->
    <el-alert
      v-if="enableOnlineStatus"
      id="online-status-wrapper"
      :title="onlineMessage"
      :closable="false"
      show-icon
      type="success"
    />

    <!-- Video Lesson -->
    <VideoLesson v-show="isToEnableVideoLesson && showVideo"/>

    <template v-if="enableVideo">
      <div v-if="remoteUsers.length" class="remote-name">
        <img v-if="!remoteMicEnabled" :src="require('@/assets/img/mic_icn-off@2x.png')" height="25" width="25">
        <span>{{ remoteNickname }}</span>
      </div>

      <p class="recorder-time" v-if="this.recordingTime > 0">{{ timer }}</p>

      <Network />

      <!-- Local Video -->
      <VideoButtons
        class="video-sm-button"
        :rtcClient="rtcClient"
        :isLocal="true"
      />
      <div
        :class="[hasLocalAndRemoteStream ? 'video-sm' : 'video-lg', 'local-video']"
        :style="{ height:videoWrapperHeight }"
        v-if="rtcClient && localUser"
        @dblclick="toggleLargeVideo"
      >
        <span class="video-wrapper" @dblclick.stop="setIsLocalFitted(!isLocalFitted)" style="-webkit-transform:translate3d(0,0,0);">
          <span v-show="showLocalVideo">
            <video v-if="isAmazonChime"
              muted="muted"
              autoplay
              playsinline
              v-bind:id="videoElementLocalTileId"
              class="video-call-feed"
              :controls="false"
              :class="{ fitted: isLocalFitted, 'share-screen-active': isShareScreenOn }"
            />
            <video v-else
              muted="muted"
              :srcObject.prop="localUser.stream"
              autoplay
              playsinline
              class="video-call-feed"
              :controls="false"
              :class="{ fitted: isLocalFitted, 'share-screen-active': isShareScreenOn }"
            />
          </span>
          <div v-show="!showLocalVideo" id="video-off">
            <div class="avatar">{{ localNickname.trim().charAt(0).toUpperCase() }}</div>
          </div>
        </span>
      </div>

      <!-- Remote Video -->
      <div :class="['video-lg', 'remote-video']" v-for="user in remoteUsers" :key="user.peerId" @dblclick="toggleLargeVideo">
        <template v-if="isAmazonChime">
          <span>
            <video v-if="hasLocalAndRemoteStream"
              v-bind:id="videoElementRemoteTileId"
              autoplay
              playsinline
              :class="{ fitted: isRemoteFitted, 'share-screen-active': remoteShareScreen }"
            />
            <div v-if="(!remoteCameraEnabled && !remoteShareScreen) || audioOnly" id="video-off">
              <div class="avatar">{{ remoteNickname.trim().charAt(0).toUpperCase() }}</div>
            </div>
          </span>
        </template>
        <template v-else>
          <span>
            <video v-if="hasLocalAndRemoteStream"
              id="remoteVideo"
              :srcObject.prop="user.stream"
              muted
              autoplay
              playsinline
              :class="{ fitted: isRemoteFitted, 'share-screen-active': remoteShareScreen }"
            />
            <div v-if="(!remoteCameraEnabled && !remoteShareScreen) || audioOnly" id="video-off">
              <div class="avatar">{{ remoteNickname.trim().charAt(0).toUpperCase() }}</div>
            </div>
          </span>
        </template>
      </div>

      <div class="video-btn-2">
        <div>
          <el-button
            :class="['reconnect', {disabled : isLessonWithinGracePeriod, 'reload-active' : isVideoReloading}]"
            :disabled="isLessonWithinGracePeriod"
            @click="reconnect()"
            circle
          >
            <img :src="require(`@/assets/img/refresh_${refreshIcon}@2x.png`)" />
          </el-button>
          <p :class="{'disabled-text' : isLessonWithinGracePeriod}">{{ $t('buttons.reload') }}</p>
        </div>

        <ScreenShare @reset="reRenderVideo" />

        <Recorder :remote-users="remoteUsers"  />

        <div class="video-fullscreen">
          <el-button @click="fullScreenVideo" circle>
            <img :src="zoomIcon" />
          </el-button>
        </div>
      </div>
    </template>

    <div v-else>
      <span id="video-label" v-html="videoMessage" v-show="!isToEnableVideoLesson || !showVideo"></span>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { layoutStyle, programs, MAX_RTC_INIT_RELOADS } from '@/utils/constants';
import { store, toObjectLiteral } from '@/utils/common'
import { storeSession } from '@/utils/storeSession'

import gracePeriodMixin from '@/mixins/gracePeriod';
import virtualBgMixin from '@/mixins/virtualBg';
import rtcClient from '@/modules/webrtc/RtcClient';

import VideoButtons from '@/components/Lesson/VideoChat/VideoButtons';
import ScreenShare from '@/components/Lesson/VideoChat/ScreenShare';
import Network from '@/components/Lesson/VideoChat/Network';
import Recorder from '@/components/Lesson/VideoChat/Recorder';
import VideoLesson from '@/components/Lesson/VideoChat/VideoLesson';
import { providerIds }  from '@/utils/constants';
import { buildJoinMeta, joinPrefix } from '@/modules/webrtc/joinLogUtils';

export default {
  components: {
    VideoButtons,
    Network,
    ScreenShare,
    Recorder,
    VideoLesson
  },
  mixins: [gracePeriodMixin, virtualBgMixin],
  data: () => {
    return {
      peer: null,
      remoteUsers: [],
      beforeLessonStart: true,
      videoWrapperHeight: 'auto',
      reason: '',
      hideModal: false,
      videoElementLocalTileId: '',
      videoElementRemoteTileId: '',
      chimeAudioElement: {},
      initialUsers: 1,
      isRetrying: false
    };
  },
  // the parameter from parent component
  computed: {
    ...mapState('videoRtc', [
      'rtcClient',
      'localUser',
      'peerError',
      'isShareScreenOn',
      'deviceRtcError',
      'virtualBg',
      'virtualBgModel',
      'audioOnly',
      'uidRetryCount'
    ]),
    ...mapState('connection', [
      'canReconnect',
      'connectionProblem',
      'internetStatus',
      'refreshed'
    ]),
    ...mapState('lesson', [
      'isLessonBecameOngoing',
      'isLessonWithinGracePeriod',
      'isLessonBeyondGracePeriod',
    ]),
    ...mapState('layout', [
      'isVideoFullScreen',
      'layoutColor',
      'isMobileSize',
      'isRemoteFitted',
      'isLocalFitted',
      'isVideoReloading'
    ]),
    ...mapState('video', ['showVideo']),
    ...mapState('devices', ['cameraEnabled', 'micEnabled']),
    ...mapState([
      'userId',
      'lessonId',
      'remoteUserId',
      'provider',
      'localNickname',
      'remoteNickname',
      'lessonEnd',
      'lessonStatus',
      'videoProfile',
      'screenShareProfile',
      'programId',
      'localExternalId',
      'remoteExternalId',
      'hasTranscription',
      'hasRecording',
      'accType',
      'enabledCloudRecording',
      'webRtcUid'
    ]),
    ...mapState('socketio', [
      'socket',
      'simultaneousConnection',
      'remoteShareScreen',
      'remoteCameraEnabled',
      'lessonResultCode',
      'voluntaryLessonTermination',
      'substituteTrainer',
      'remoteMicEnabled',
      'onlineUsers'
    ]),
    ...mapState('recorder', ['recordingTime']),
    ...mapGetters([
      'isLessonOngoing',
      'isLessonEnded',
      'isWarmUpPeriod',
      'isWaitingOrWarmUpPeriod',
      'isStudent',
      'isToEnableVideoLesson',
      'isTrainer',
      'isAssessmentLesson',
      'isAmazonChime',
      'isZipanService'
    ]),
    ...mapGetters('socketio', [
      'hadTrainerSubstitution',
      'unterminatedLessonResult'
    ]),
    ...mapGetters('devices', ['camera', 'mic']),
    ...mapGetters('connection', ['showReconnectingOverlay']),
    ...mapGetters('videoRtc', [
      'isMediaInvalid',
      'isUidConflict',
      'isUidBanned'
    ]),
    ...mapState('material', ['currentRank', 'currentLesson', 'pdfUrl']),
    videoClass() {
      return {
        disable: !this.enableVideo,
        fullscreen: this.isVideoFullScreen
      };
    },
    refreshIcon() {
      return this.isLessonWithinGracePeriod || this.isVideoReloading ? 'dark' : this.layoutColor;
    },
    zoomIcon() {
      const zoom = this.isVideoFullScreen ? 'zoomout' : 'zoom';
      return require(`@/assets/img/movie_${zoom}_${this.layoutColor}@2x.png`);
    },
    videoMessage: {
      get: function() {
        if (this.reason == '') {
          if (!this.internetStatus) {
            return this.$t('videoMessage.noInternetConnection');
          }
          if (this.connectionProblem) {
            return this.$t('videoMessage.connectionProblem');
          }
          if (this.isWaitingOrWarmUpPeriod) {
            return this.$t('videoMessage.waitingForLesson');
          }
          if (this.simultaneousConnection || this.isUidBanned) {
            return this.$t('videoMessage.simultaneousConnection');
          }
          if (this.hasStreamError) {
            return this.$t('videoMessage.streamError.' + this.hasStreamError);
          }
          if (this.waitingForOtherParty) {
            return this.$t(`videoMessage.waitingForOtherParty.${this.accType}`);
          }
          if (this.lessonResultCode > 1) {
            return this.lessonResultDetails();
          }
          return this.$t('videoMessage.lessonEnded');
        }
        return this.reason;
      },
      set: function(newValue) {
        this.reason = newValue;
      }
    },
    credential() {
      return this.$store.state.credential;
    },
    waitingForOtherParty() {
      if (this.refreshed) {
        return false;
      }
      
      return this.isLessonBecameOngoing && this.beforeLessonStart
        ? !this.remoteUsers.length
        : false;
    },
    hasLocalAndRemoteStream() {
      return this.remoteUsers.length > 0 && this.localUser.peerId;
    },
    enableVideo() {
      return (
        this.canReconnect &&
        this.internetStatus &&
        !this.connectionProblem &&
        !this.isWaitingOrWarmUpPeriod &&
        !this.waitingForOtherParty &&
        !this.simultaneousConnection &&
        !this.displayableLessonResult &&
        !this.hasStreamError
      );
    },
    hasStreamError() {
      if (!this.peerError) {
        return false;
      }

      if (this.isMediaInvalid) {
        this.getCameras();
        this.getMics();
        return false;
      }

      return 'unexpectedError';
    },
    displayableLessonResult() {
      const lessonWithTrainerSubHasStarted = this.isLessonBecameOngoing && this.hadTrainerSubstitution;
      return this.lessonResultCode && !lessonWithTrainerSubHasStarted;
    },
    showLocalVideo() {
      return this.cameraEnabled || this.isShareScreenOn
    },
    timer() {
      let minutes = Math.floor(this.recordingTime / 60)
        .toString()
        .padStart(2, '0');
      let seconds = (this.recordingTime % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    },
    onlineMessage() {
      return this.$t(`videoMessage.onlineStatus.${this.accType}`);
    },
    enableOnlineStatus() {
      console.log('Online users - ' + this.onlineUsers.length + ' and waiting period is ' + this.isWaitingOrWarmUpPeriod);
      return (this.onlineUsers.length > 1 && this.isWaitingOrWarmUpPeriod ? true : false);
    },

    uidErrorDetected() {
      return this.isUidConflict || this.isUidBanned;
    },
  },
  watch: {
    onlineUsers: {
      handler(data) {
        if (data.length && data.length != this.initialUsers) {
          this.initialUsers = data.length;
          this.socket.emit('trigger-online');
        }
      },
      deep: true
    },
    canReconnect(canReconnect) {
      if (!canReconnect) {
        this.beforeLessonStart = false;
        this.disconnect();
      }
    },
    isVideoFullScreen: {
      handler: function() {
        this.setVideoWrapperHeight();
      }
    },
    isWarmUpPeriod(status) {
      if (status) {
        this.generateRtcClient();
      }
    },
    isLessonBecameOngoing(status) {
      if (status) {
        // when lesson has started, removed the video message info
        this.reason = this.hadTrainerSubstitution ? '' : this.reason;
        if (window.player) {
          window.player.accessor.pause();
        }

        if (this.remoteUsers.length > 0) {
          this.beforeLessonStart = false;
        }

        if (!this.rtcClient) {
          this.generateRtcClient();
        }
      }
    },
    remoteUsers: {
      handler: function() {       
        if (this.lessonStartedWithBothParty()) {
          this.beforeLessonStart = false;
          if (this.isAmazonChime && !this.isZipanService) {
            if (store.getTranscribeState(this.lessonId, this.hasTranscription)) return;

            if (this.isStudent && !this.hasTranscription) this.activateTranscribe({ lessonId: this.lessonId, userId: this.userId, webRTCId: providerIds.awschime });
          }

          if (this.provider == "agora" && !this.isZipanService) {
            if (store.getAgoraRecordState(this.lessonId, this.hasRecording)) return;

            if (this.isStudent && !this.hasRecording) this.activateAgoraRecord({ lessonId: this.lessonId, userId: this.userId, webRTCId: providerIds.agora });
          }
        }

        if (this.isAmazonChime && !this.isZipanService) {
          if (store.getTranscribeState(this.lessonId, this.hasTranscription)) return;

          if (this.isTrainerWaitedForStudentToJoin()) this.activateTranscribe({ lessonId: this.lessonId, userId: this.userId, webRTCId: providerIds.awschime });
        }

        if (this.provider == "agora" && !this.isZipanService) {
          if(store.getAgoraRecordState(this.lessonId, this.hasRecording))  return;

          if (this.isTrainerWaitedForStudentToJoin()) this.activateAgoraRecord({ lessonId: this.lessonId, userId: this.userId, webRTCId: providerIds.agora });
        }

        // when the remote user is connected
        // trigger a material update if we are a TRAINER
        // if this is an assessment lesson, we will set the material
        // for the student to see which lesson and rank they are on
        if (this.isTrainer) {
          this.$logger.debug('Remote users list updated, ping our student(non-trainer) user with our latest lesson material details', { operation: 'debug', debug: {
            isAssessmentLesson: this.isAssessmentLesson,
            currentRank: this.currentRank,
            currentLesson: this.currentLesson,
            pdfUrl: this.pdfUrl,
            isTrainer: true,
            lessonId: this.lessonId
          } });
          this.setOmittedMaterial({
            isAssessmentLesson: this.isAssessmentLesson,
            currentRank: this.currentRank,
            currentLesson: this.currentLesson,
            pdfUrl: this.pdfUrl,
            isTrainer: true
          });
        }
      }
    },
    simultaneousConnection(status) {
      if (status) {
        this.$logger.info('Simultaneous connection detected', { operation: 'connection:terminate', result: 1 });
        this.disconnect();
      }
    },
    voluntaryLessonTermination(status) {
      if (status) {
        this.$logger.info('Voluntary lesson termination occured due to remote user leaving within grace period', {
          operation: 'connection:terminate',
          result: 2
        });
        this.setCanReconnect(false);
        this.setRecording(false);
        this.toggleVideoLesson(true);
        this.stopRecorder();
      }
    },
    remoteShareScreen(status) {
      if (status) {
        this.switchLayoutStyle(layoutStyle.videoScreen);
        this.setIsRemoteFitted(false);
      } else if (!status && this.isAssessmentLesson) {
        this.switchLayoutStyle(layoutStyle.default);
        this.setIsRemoteFitted(true);
      }
    },
    lessonResultCode() {
      if (!this.unterminatedLessonResult) {
        this.reason = this.lessonResultDetails();
        this.handleLessonResult();
      } else {
        this.$logger.info('Received terminated lesson result', { operation: 'connection:terminate', result: 3 });
      }
    },
    waitingForOtherParty(status) {
      if (!status) {
        this.socket.emit('signal', { type: 'camera', state: this.cameraEnabled })
        this.socket.emit('signal', { type: 'mic', state: this.micEnabled })
      }
    },
    isLessonOngoing(status) {
      if (status) {
        this.toggleVideoLesson(false);
      }
    },
    enableVideo(state) {
      if (!state) {
        return;
      }
      if (this.isAmazonChime) {
        this.rtcClient.updateVideoTiles();
        this.chimeAudioElement.muted = false;
        return;
      }
      this.rtcClient?.toggleMic(this.micEnabled);
    },
    isLessonBeyondGracePeriod(status) {
      if (status) {
         this.toggleVideoLesson(true)
      }
    },
    deviceRtcError(state) {
      if (state) {
        this.disconnect();
      }
    },
    async uidErrorDetected(val) {
      if (!val) return;

      this.isRetrying = true;
      this.setUidRetryCount(this.uidRetryCount + 1);
      const result = await this.handleUidConflictRetry(this.webRtcUid);

      console.log('[uidErrorDetected] result from backend:', result);

      if (result) {
        this.setCredential(result.token);
        this.setWebRtcUid(result.uid);
        this.$logger.info('UID conflict retry succeeded', {
          operation: 'webrtc:uid-conflict',
          attempt: this.uidRetryCount,
          lesson_id: this.lessonId
        });
        this.reRenderVideo();
      }

      this.isRetrying = false;
    },
    isLessonEnded(ended) {
      if (ended) {
        this.setUidRetryCount(0);
      }
    }
  },
  created() {
    this.videoElementLocalTileId = 'meeting-video-' + this.localExternalId;
    this.videoElementRemoteTileId = 'meeting-video-' + this.remoteExternalId;
  },
  mounted() {
    this.initialize();
    this.setVideoWrapperHeight();
    window.addEventListener('resize', () => {
      this.setVideoWrapperHeight();
    });

    switch(this.programId) {
      case programs.discovery:
      case programs.assessment:
      case programs.assist_lesson:
        this.toggleVideoLesson(false);
        break;
      default:
        this.toggleVideoLesson(this.isToEnableVideoLesson);
    }
  
    this.chimeAudioElement = document.getElementById('meeting-audio') ?? {};
  },
  methods: {
    ...mapActions('devices', ['getMics', 'getCameras']),
    ...mapActions('videoRtc', ['handleUidConflictRetry']),
    ...mapMutations(['setCredential', 'setWebRtcUid']),
    ...mapMutations('videoRtc', ['setRtcClient', 'setLocalUserStream', 'setPeerError', 'setUidRetryCount']),
    ...mapMutations('connection', ['setCanReconnect', 'setRefreshed']),
    ...mapMutations('layout', [
      'videoFullScreen',
      'setIsRemoteFitted',
      'setIsLocalFitted',
      'setIsVideoReloading'
    ]),
    ...mapMutations('socketio', ['setSimultaneousConnection', 'setOmittedMaterial']),
    ...mapMutations('recorder', ['setRecording']),
    ...mapActions('layout', ['switchLayoutStyle']),
    ...mapActions(['getLessonInfo', 'activateTranscribe', 'activateAgoraRecord']),
    ...mapActions('video', ['toggleVideoLesson']),
    ...mapActions('recorder', ['stopRecorder']),

    reRenderVideo() {
      this.$logger.info(`Emit reconnect`, { operation: 'webrtc:share-screen' });
      this.$emit('reconnect');
    },

    setVideoWrapperHeight() {
      if (document.querySelector('#header')) {
        const height = window.innerHeight - document.querySelector('#header').offsetHeight - 60;
        this.videoWrapperHeight = this.isVideoFullScreen && this.$isMobileSize() ? height + 'px' : 'auto';
      }
    },

    fullScreenVideo() {
      this.videoFullScreen();
      this.setVideoWrapperHeight();
    },

    // initialized the webrtc
    initialize() {
      if ((this.isLessonOngoing && !this.isLessonWithinGracePeriod) || this.isWarmUpPeriod) {
        this.generateRtcClient();
      } else if (this.isLessonEnded) {
        this.disconnect();  
      }
    },

    // generate client by adaptor
    async generateRtcClient() {
      let micId = store.getMicId(this.userId);
      let cameraId = store.getCameraId(this.userId);
      let speakerId = store.getSpeakerId(this.userId);
      this.$logger.debug('Media devices local storage values', { micId, cameraId, speakerId });

      const PREFIX = joinPrefix('VideoChat');
      this.$logger.info(`${PREFIX} generateRtcClient started - provider: ${this.provider}, lessonId: ${this.lessonId}`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));

      const videoEffect = store.getBlurState();
      if (videoEffect !== 'none') {
        this.$logger.info(`${PREFIX} Loading virtual background model`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));
        await this.loadVBModel();
      }

      this.$logger.info(`${PREFIX} Creating RtcClient`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));
      const rtc = await new rtcClient({
        userId: this.webRtcUid,
        remoteUserId: this.remoteUserId,
        credential: this.credential,
        provider: this.provider,
        remoteUsers: this.remoteUsers,
        videoProfile: this.videoProfile,
        screenShareProfile: this.screenShareProfile,
        enableAudio: this.micEnabled,
        enableVideo: this.cameraEnabled,
        socket: this.socket,
        cameraId: cameraId,
        micId: micId,
        speakerId: speakerId,
        videoEffect: videoEffect,
        virtualBgModel: this.virtualBgModel,
        remoteExternalId: this.remoteExternalId,
        lessonId: this.lessonId
      });
      this.setRtcClient(rtc);

      if (this.isWarmUpPeriod) {
        this.chimeAudioElement.muted = true;
      }
      
      // adaptor init failed
      if (this.rtcClient.initError) {
        this.setRtcClient(null);

        if (!storeSession.isAvailable()) {
          this.$logger.error('rtcClient initialize error, sessionStorage unavailable, skipping reload', {
            operation: 'webrtc:initialize',
            event_type: 14,
            lesson_id: this.lessonId
          });
          return;
        }

        const reloadCount = storeSession.getRtcInitReloadCount(this.lessonId);

        if (reloadCount < MAX_RTC_INIT_RELOADS) {
          storeSession.setRtcInitReloadCount(this.lessonId, reloadCount + 1);
          this.$logger.error('rtcClient initialize error, reloading page', {
            operation: 'webrtc:initialize',
            event_type: 14,
            lesson_id: this.lessonId,
            reload_attempt: reloadCount + 1
          });
          window.location.reload();
          return;
        }

        this.$logger.error('rtcClient initialize error, max reload attempts reached', {
          operation: 'webrtc:initialize',
          event_type: 14,
          lesson_id: this.lessonId,
          reload_attempt: reloadCount
        });
        return;
      }

      // Clear reload count on successful init
      storeSession.clearRtcInitReloadCount(this.lessonId);

      this.$logger.info(`${PREFIX} RtcClient initialized, joining room`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));

      // join in the room
      this.peer = await this.rtcClient.join(this.lessonId);

      // Check for errors upon joining the channel
      if (!this.peer.hasOwnProperty('error')) {
        this.$logger.info(`${PREFIX} Join succeeded, setting up local stream`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));
        this.setLocalUserStream(this.peer);
        this.rtcListener();
        // add manual toggle to start the video/camera immediately
        this.rtcClient.toggleCamera(this.cameraEnabled);
        this.rtcClient.toggleMic(this.micEnabled);
      } else {
        this.setPeerError(this.peer.error);
        this.$logger.error('Error on publishing stream', {
          operation: 'webrtc:publish-stream',
          err: toObjectLiteral(this.peer.error),
          event_type: 10,
          lesson_id: this.lessonId
        });
        this.$logger.error(`${PREFIX} Join failed`, buildJoinMeta({ provider: this.provider, lessonId: this.lessonId }));
      }
      this.setIsVideoReloading(false);
    },

    rtcListener() {
      // Listen for any error
      if (this.peer && this.peer.rtc) {
        this.peer.rtc.on('error', error => {
          if (this.peerIdIsAlreadyInUse(error)) {
            this.setSimultaneousConnection(true);
          }
          this.$logger.error('WebRTC error event', { operation: 'webrtc:error-event', error,event_type:15,lesson_id:this.lessonId });
        });
      }
    },

    // Check if peer id is already in use for Skyway
    peerIdIsAlreadyInUse(error) {
      if (!error.message) return false;
      let peerIdInUsed = `PeerId "${this.userId}" is already in use`;
      return error.message.includes(peerIdInUsed);
    },

    // disconnect
    disconnect: function() {
      if (this.rtcClient == null) {
        return;
      }
      if (this.remoteUsers.length) {
        this.remoteUsers = [];
      }
      if (!this.rtcClient.initError) {
        this.rtcClient.disconnect();
        this.setRtcClient(null);
        this.peer = null;
      }
    },

    // Emit reconnect function on parent component
    reconnect: function() {
      if (this.isVideoReloading) {
        return;
      }
      // Don't allow reconnect while offline or during active reconnection —
      // keep the overlay visible and let auto-reconnect handle recovery
      if (!navigator.onLine || this.showReconnectingOverlay) {
        this.$logger.info('Reconnect blocked - offline or reconnection in progress', { operation: 'webrtc:reconnect' });
        return;
      }
      this.setRefreshed(true);
      this.$emit('reconnect');
      this.$logger.info('Reconnect to webRTC', { operation: 'webrtc:reconnect' });
    },

    // Lesson has just started and both party are present
    lessonStartedWithBothParty() {
      return this.isLessonBecameOngoing && this.remoteUsers.length > 0;
    },

    toggleLargeVideo() {
      if (this.hasLocalAndRemoteStream) {
        this.setIsRemoteFitted(!this.isRemoteFitted);
      } else {
        this.setIsLocalFitted(!this.isLocalFitted);
      }
    },

    async handleLessonResult() {
      // on trainer substitution, re-request the lesson token for student
      if (this.hadTrainerSubstitution && this.isStudent) {
        await this.getLessonInfo({ lessonId: this.lessonId });
        this.socket.emit('ack', { type: 'lesson-result', d: false });
      } else {
        this.socket.emit('ack', { type: 'lesson-result', d: true });
        this.disconnect();
        this.setCanReconnect(false);
        this.socket.disconnect(true);
      }
    },

    lessonResultDetails() {
      if (this.$te(`lessonResult.${this.lessonResultCode}`)) {
        return this.hadTrainerSubstitution
          ? this.$t(`lessonResult.${this.lessonResultCode}`, [this.substituteTrainer])
          : this.$t(`lessonResult.${this.lessonResultCode}`);
      }
      return `${this.$t('lessonResult.unknown')} (${this.lessonResultCode})`;
    },
    isTrainerWaitedForStudentToJoin() {
      if (this.isAmazonChime) {
        return (
          !this.isLessonBecameOngoing && 
          this.remoteUsers.length > 0 && 
          !this.hasTranscription && 
          this.isStudent
        );
      }

      if (this.provider == "agora") {
        return (
          !this.isLessonBecameOngoing && 
          this.remoteUsers.length > 0 && 
          !this.hasRecording && 
          this.isStudent
        );
      }
    },
  },
  // beforeDestroy
  beforeDestroy: function() {
    this.disconnect();
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped src="@/assets/css/pc/video_chat.css"></style>
<style scoped src="@/assets/css/sp/video_chat.css"></style>
<style scoped src="@/assets/css/pc/online_status.css"></style>
<style scoped src="@/assets/css/sp/online_status.css"></style>

<style scoped>
.reconnecting-overlay {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.85) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 100 !important;
  pointer-events: none;
}

.reconnecting-message {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  gap: 8px;
  color: #ffffff !important;
  text-align: center;
}

.reconnecting-title {
  font-size: 16px;
  font-weight: 500;
}

.reconnecting-subtext {
  font-size: 10px;
  line-height: 10px;
  opacity: 0.85;
}

.reconnecting-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: reconnecting-spin 0.8s linear infinite;
  margin-bottom: 4px;
}

@keyframes reconnecting-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
